import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { RiseInDropOut } from '@entrata/portals-common/lib/components/Transitions/RiseInDropOut'
import { HandleClickOutside } from '@entrata/portals-common/lib/components/HandleClickOutside'
import {
  Download,
  DownTriangle,
  Document,
} from '@entrata/portals-common/lib/icons'
import { LEDGER_DOWNLOAD_FORMATS } from 'portal-modules/ledger/utils/ledger-download'
import { LEDGER_TOOLBAR_CONTROL_HEIGHT } from 'portal-modules/ledger/constants/keys'

// Single source of truth for the selectable export formats so adding a
// third format (e.g. XLSX) is a one-line change here. The popover renders a
// real SVG icon per row (see below), unlike the previous native <select>
// which could only hold a unicode glyph.
const FORMAT_OPTIONS = [
  { id: LEDGER_DOWNLOAD_FORMATS.PDF, labelKey: 'PDF Statement' },
  { id: LEDGER_DOWNLOAD_FORMATS.CSV, labelKey: 'CSV' },
]

const Wrapper = styled('div')`
  position: relative;
  display: inline-flex;
  align-items: center;
`

// The dark "DOWNLOAD" pill trigger: dark fill, white uppercase label,
// rounded corners, leading download glyph and a trailing chevron. Pinned to
// the shared toolbar control height so it stays edge-aligned with the
// scope-tab pill group.
const TriggerButton = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: ${LEDGER_TOOLBAR_CONTROL_HEIGHT};
  padding: 0 16px;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.mode.card_border_radius};
  background: ${({ theme }) => theme.mode.tab_background_color};
  color: ${({ theme }) => theme.text.white};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px
      ${({ theme }) =>
        theme.mode.isDark
          ? theme.mode.tab_focus_shadow_dark
          : theme.mode.tab_focus_shadow_light};
  }

  svg {
    color: currentColor;
    fill: currentColor;
  }
`

const ChevronIcon = styled(DownTriangle)`
  margin-left: 2px;
`

// Floating anchor for the popover. Positioning lives here (not on the
// animated element) so RiseInDropOut only animates the panel in place — this
// keeps the menu pinned below the trigger and right-aligned to it while the
// enter/leave animation plays.
const PopoverAnchor = styled('div')`
  position: absolute;
  top: calc(${LEDGER_TOOLBAR_CONTROL_HEIGHT} + 8px);
  right: 0;
  z-index: 1001;
`

// The popover card itself: a light surface with header/divider/rows. This is
// the reason the control moved off the native <select> — an OS-drawn option
// list cannot render the header/divider/per-row icons below.
const Menu = styled('div')`
  min-width: 240px;
  padding: 8px;
  background: ${({ theme }) => theme.card.background};
  border: 1px solid ${({ theme }) => theme.mode.card_border_color};
  border-radius: ${({ theme }) => theme.mode.card_border_radius};
  box-shadow: 0 12px 32px ${({ theme }) => theme.mode.popover_shadow};
  box-sizing: border-box;
`

const MenuHeader = styled('div')`
  padding: 8px 12px 4px;
`

const MenuTitle = styled('div')`
  color: ${({ theme }) => theme.text.white};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
`

const MenuSubtitle = styled('div')`
  color: ${({ theme }) => theme.text.muted_foreground};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
  margin-top: 2px;
`

const MenuDivider = styled('div')`
  height: 1px;
  margin: 8px 0;
  background: ${({ theme }) => theme.mode.card_border_color};
`

const MenuItem = styled('button')`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: ${({ theme }) => theme.mode.card_border_radius};
  background: transparent;
  color: ${({ theme }) => theme.text.white};
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${({ theme }) => theme.mode.menu_item_hover_background};
  }

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.text.muted_foreground};
    fill: ${({ theme }) => theme.text.muted_foreground};
  }
`

// Download control for the Tenant Portal Ledger toolbar. Built as a custom
// anchored popover from shared design-system parts: `RiseInDropOut` for the
// open/close animation and `HandleClickOutside` for dismissal. Selection is
// the single integration point — choosing a format notifies `onSelect`,
// which `LedgerTabnav` wires to the `exportLedger` pipeline, then the popover
// closes.
const LedgerDownloadDropdown = ({
  onSelect = undefined,
  disabled = false,
  className = undefined,
  filteredCount = undefined,
  totalCount = undefined,
}) => {
  const [open, setOpen] = useState(false)

  const closeMenu = () => setOpen(false)

  // Close on Escape while open. Outside clicks are handled by
  // HandleClickOutside below.
  useEffect(() => {
    if (!open) return undefined
    const handleKeyDown = event => {
      if (event.key === 'Escape') closeMenu()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open])

  const handleToggle = () => {
    if (disabled) return
    setOpen(prev => !prev)
  }

  const handleSelect = format => {
    if (typeof onSelect === 'function') onSelect(format)
    closeMenu()
  }

  const hasMeaningfulCount =
    typeof filteredCount === 'number' && typeof totalCount === 'number'

  // Two-line header matching the prototype: bold "Tenant Ledger" title with a
  // muted "{filtered} of {total} transactions (current filter)" subtitle.
  // Pluralisation is keyed off totalCount; __() supports positional
  // placeholders only.
  const subtitle = (() => {
    if (!hasMeaningfulCount) return ''
    return totalCount === 1
      ? __('{%d, 0} of {%d, 1} transaction (current filter)', [
          filteredCount,
          totalCount,
        ])
      : __('{%d, 0} of {%d, 1} transactions (current filter)', [
          filteredCount,
          totalCount,
        ])
  })()

  return (
    <HandleClickOutside onClick={closeMenu}>
      {({ id, ref }) => (
        <Wrapper className={className} id={id} ref={ref}>
          <TriggerButton
            type='button'
            onClick={handleToggle}
            disabled={disabled}
            aria-haspopup='menu'
            aria-expanded={open}
            aria-label={__('Download ledger')}
            data-testid='ledger-download-trigger'
          >
            <Download size={16} />
            {__('Download')}
            <ChevronIcon size={16} />
          </TriggerButton>

          <PopoverAnchor>
            <RiseInDropOut show={open}>
              <Menu
                role='menu'
                aria-label={__('Download ledger')}
                data-testid='ledger-download-menu'
              >
                <MenuHeader>
                  <MenuTitle data-testid='ledger-download-menu-header'>
                    {__('Tenant Ledger')}
                  </MenuTitle>
                  {!!subtitle && (
                    <MenuSubtitle data-testid='ledger-download-menu-subheader'>
                      {subtitle}
                    </MenuSubtitle>
                  )}
                </MenuHeader>
                <MenuDivider data-testid='ledger-download-menu-divider' />
                {FORMAT_OPTIONS.map(option => (
                  <MenuItem
                    key={option.id}
                    type='button'
                    role='menuitem'
                    onClick={() => handleSelect(option.id)}
                    data-testid={`ledger-download-format-${option.id}`}
                  >
                    <Document size={18} />
                    {__(option.labelKey)}
                  </MenuItem>
                ))}
              </Menu>
            </RiseInDropOut>
          </PopoverAnchor>
        </Wrapper>
      )}
    </HandleClickOutside>
  )
}

LedgerDownloadDropdown.propTypes = {
  onSelect: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  filteredCount: PropTypes.number,
  totalCount: PropTypes.number,
}

export { LedgerDownloadDropdown }
export { LEDGER_DOWNLOAD_FORMATS } from 'portal-modules/ledger/utils/ledger-download'
